import classes from "./FormApprovalUnfinished.module.css";
import { useTranslation } from "react-i18next";
import { useCurrentDataContext } from "../../../contexts/CurrentDataContext";
import { useUserContext } from "../../../contexts/UserContext";
import SaveUnfinishedButton from "../Buttons/SaveUnfinishedButton/SaveUnfinishedButton";
import FormNotActive from "../FormNotActive/FormNotActive";
import { countCompletedDemonstrations } from "../../../scripts/countCompletedDemonstrations";
import {
  Accomplishment,
  PermitFormBody,
  SearchLabel,
} from "../../../contexts/interfaces";
import { useEffect, useState } from "react";
import Select from "react-select";
import { getSelectableDoctors } from "../../../scripts/dataHandlers";
import { useRequiredAccomplishmentsContext } from "../../../contexts/RequiredAccomplishmentsContext";
import { useErrorContext } from "../../../contexts/ErrorContext";
import { validateRequiredAccomplishments } from "../../../utils/accomplishmentDateRules";

interface Props {
  formSubmitHandler: (
    e: React.SyntheticEvent<HTMLFormElement> | undefined,
    step: string
  ) => void;
  setLoading: (val: boolean) => void;
  employeeAccomplishments: Accomplishment[];
  employeeExpenseUnitId: number;
}

interface RequiredAccomplishment {
  type: string;
  title: string;
}

interface SelectableDoctor {
  id: number;
  fullTitle: string;
}

const FormApprovalUnfinished = ({
  formSubmitHandler,
  setLoading,
  employeeAccomplishments,
  employeeExpenseUnitId,
}: Props) => {
  const { t } = useTranslation();

  // Context for form data
  const { currentData, setCurrentData } = useCurrentDataContext();

  const [missingDemonstrations, setMissingDemonstrations] = useState<
    PermitFormBody[]
  >([]);
  const [missingAccomplishments, setMissingAccomplishments] = useState<
    RequiredAccomplishment[]
  >([]);
  const [expiredAccomplishments, setExpiredAccomplishments] = useState<
    RequiredAccomplishment[]
  >([]);
  const [continueDisabled, setContinueDisabled] = useState<boolean>(true);

  const { requiredAccomplishments } = useRequiredAccomplishmentsContext();

  const { handleError } = useErrorContext();

  const [searchOptions, setSearchOptions] = useState<SearchLabel[]>();

  // Context for userData
  const { user } = useUserContext();

  const checkAccomplishments = () => {
    if (!requiredAccomplishments) {
      return false;
    }

    const result = validateRequiredAccomplishments(
      requiredAccomplishments,
      employeeAccomplishments,
      {
        checkFormData: true,
        currentData,
      }
    );

    setMissingAccomplishments(result.missing);
    setExpiredAccomplishments(result.expired);
    return result.valid;
  };

  // UseEffect, which gets selectable doctors
  useEffect(() => {
    const getSelectableDoctorsFromAPI = async () => {
      const res =
        user &&
        employeeExpenseUnitId &&
        (await getSelectableDoctors(user.id, employeeExpenseUnitId));

      if (res && res?.status === 200) {
        setSearchOptions([
          ...res.data.result.map((option: SelectableDoctor) => {
            return {
              label: `${option.fullTitle}`,
              value: String(option.id),
            };
          }),
        ]);
      } else {
        setSearchOptions([]);
        handleError(res, "GetSelectableDoctorsFailed");
      }
    };

    user &&
      currentData?.formStatus === "approval" &&
      getSelectableDoctorsFromAPI();
  }, 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [user, employeeExpenseUnitId, currentData?.formStatus]);
  // UseEffect, which checks disabled status for submit button.
  // If fields are empty or register number is given but also "no register number"
  // is checked, it will disable submit button
  useEffect(() => {
    // Set empty array for missing demos
    let checkMissingDemos: PermitFormBody[] = [];

    // Check each permit from currentData.
    // If permit is checked, count completed demonstrations for it and push
    // permit into checkMissingPermits array, if something is missing
    currentData?.permits?.forEach((permit) => {
      if (
        permit.isChecked === true &&
        countCompletedDemonstrations(permit) < 3
      ) {
        checkMissingDemos.push(permit);
      }
    });

    // Check if accomplishments are valid
    const accomplishmentsAreValid =
      employeeAccomplishments &&
      requiredAccomplishments &&
      checkAccomplishments();

    // Set checkMissingPermits array to missingDemonstrations stateholder
    setMissingDemonstrations([...checkMissingDemos]);

    // If doctor selection is missing or there's missing demonstrations / accomplishments, disable continue
    if (
      currentData?.assignedDoctorUnitId === null ||
      currentData?.replacePrevious === null ||
      missingDemonstrations.length > 0 ||
      !accomplishmentsAreValid ||
      (currentData?.useAttachments &&
        currentData?.records &&
        currentData?.records.findIndex(
          (record) => record.type === "accomplishment"
        ) === -1)
    ) {
      setContinueDisabled(true);
    } else {
      setContinueDisabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData]);

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    setLoading(true);

    // Call for form submitHandler, which updates data to database
    formSubmitHandler(e, "waiting");
  };
  if (currentData?.formStatus === "approval") {
    return (
      <div className={classes.formApprovalContainer}>
        <div className={classes.formApprovalTitle}>
          {`3 ${t("formApproval.mainTitle")}`}
        </div>
        <form
          className={classes.formApprovalInnerContainer}
          onSubmit={(e) => handleSubmit(e)}
        >
          <div>{t("formApproval.boss")}</div>
          <div className={classes.bossName}>
            {user?.firstName + " " + user?.lastName}
          </div>

          <div>{t("formApproval.doctor")}</div>
          {searchOptions && (
            <Select
              className={classes.formApprovalSelect}
              defaultValue={searchOptions.find(
                (option) =>
                  Number(option.value) === currentData.assignedDoctorUnitId
              )}
              placeholder={t("formApproval.chooseDoctor")}
              isSearchable={false}
              isClearable={false}
              options={searchOptions}
              onChange={(e) =>
                e &&
                setCurrentData({
                  ...currentData,
                  assignedDoctorUnitId: Number(e.value),
                  assignedDoctorFullTitle: e.label,
                })
              }
            />
          )}

          {!continueDisabled ? (
            <div className={classes.formApprovalConfirmationContainer}>
              <div className={classes.formApprovalConfirmationText}>
                {t("formApproval.formIsReady")}
              </div>
              <div className={classes.formApprovalConfirmationText}>
                {t("formApproval.formIsReadyAgreement")}
              </div>
            </div>
          ) : (
            <div className={classes.formApprovalConfirmationContainerNotReady}>
              <div className={classes.formApprovalConfirmationText}>
                {t("formApproval.formIsNotReady")}
              </div>
              <div className={classes.formApprovalConfirmationText}>
                {missingAccomplishments.map((accomplishment, i) => (
                  <div key={i}>
                    {`• ${t("formApproval.accomplishmentMissing", {
                      title: accomplishment.title,
                    })}`}
                  </div>
                ))}
                {expiredAccomplishments.map((accomplishment, i) => (
                  <div key={`expired-${i}`}>
                    {`• ${t("formApproval.accomplishmentExpired", {
                      title: accomplishment.title,
                    })}`}
                  </div>
                ))}
                {missingDemonstrations.map((permit, i) => (
                  <div key={i}>
                    &bull;
                    {` "${permit.fullTitle}" puuttuu ${
                      3 - countCompletedDemonstrations(permit)
                    } näyttöä`}
                  </div>
                ))}
                {
                  // Show message if there's no assigned doctorUnitId
                  currentData?.assignedDoctorUnitId === null && (
                    <div>&bull;{` Lääkärivalinta puuttuu`}</div>
                  )
                }
                {
                  // Show message if there's no replacePrevious selected
                  currentData?.replacePrevious === null && (
                    <div>
                      &bull;
                      {` Valinta olemassaolevien lupien korvaamiselle tai täydentämiselle puuttuu `}
                    </div>
                  )
                }
                {
                  // Show message if useAttachments is used to show accomplishments and if there's no added attachment for it
                  currentData.useAttachments &&
                    currentData.records &&
                    currentData.records.findIndex(
                      (record) => record.type === "accomplishment"
                    ) === -1 && (
                      <div>&bull;{` Lääkehoitosuorituksien liite puuttuu`}</div>
                    )
                }
              </div>
            </div>
          )}

          <button
            className="formContinueButton"
            type="submit"
            disabled={continueDisabled}
          >
            {t("formApproval.formContinueButton")}
          </button>

          <SaveUnfinishedButton
            formSubmitHandler={formSubmitHandler}
            step={"approval"}
          />
        </form>
      </div>
    );
  } else {
    return (
      <FormNotActive
        title={`3 ${t("formApproval.mainTitle")}`}
        text={t("formApproval.notSent")}
      />
    );
  }
};

export default FormApprovalUnfinished;
